import React, { useState } from 'react';
import { Button, Card } from '@dopomogai/ui';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X } from 'lucide-react';
import { Chatbot } from '@dopomogai/chatbot';
import type {
  FrontendToolContext,
  ToolRegistry,
  ChatbotProps,
} from '@dopomogai/chatbot';

type Props = {
  toolRegistry: ToolRegistry;
  headlessTools: ChatbotProps['headlessTools'];
  headlessContext: FrontendToolContext;
  agentAvatarUrl?: string;
};

export function BuilderAgentChat({ toolRegistry, headlessTools, headlessContext, agentAvatarUrl }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const pillVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };
  const panelVariants = {
    hidden: { opacity: 0, height: 0, y: 20 },
    visible: { opacity: 1, height: 'auto', y: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
  };

  const ClosedPill = () => (
    <motion.div variants={pillVariants} initial="hidden" animate="visible" exit="hidden" key="closed">
      <Card
        onClick={() => setIsOpen(true)}
        noPadding
        className="bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="text-emerald-700" size={24} />
            <div>
              <span className="font-extrabold text-slate-900">Interview me</span>
              <span className="text-sm text-slate-600 block">I'll help build your quote.</span>
            </div>
          </div>
          <Button size="sm" text="Start" />
        </div>
      </Card>
    </motion.div>
  );

  const OpenPanel = () => (
    <motion.div variants={panelVariants} initial="hidden" animate="visible" exit="hidden" key="open">
      <Card noPadding>
        <div className="flex items-center justify-between p-4 bg-slate-50 border-b-2 border-black">
          <p className="text-sm font-semibold text-slate-800">What would you like to do?</p>
          <Button
            variant="ghost"
            size="sm"
            icon={<X size={18} />}
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
          />
        </div>
        <div className="p-4 h-[450px] flex flex-col bg-white">
          <Chatbot
            agentId="builder-assistant-v1"
            renderMode="inline"
            mode="standalone"
            agentAvatar={agentAvatarUrl}
            initialPrompts={[
              "Set the title to 'My Awesome Agent'",
              'Add all commerce features',
              'What does the phone/IVR feature do?',
              'Generate a quote for me',
            ]}
            toolRegistry={toolRegistry}
            headlessTools={headlessTools}
            headlessContext={headlessContext}
            primaryColor="#10B981"
            memoryScope="agent"
          />
        </div>
      </Card>
    </motion.div>
  );

  return (
    <div className="relative">
      <AnimatePresence mode="wait">{isOpen ? <OpenPanel /> : <ClosedPill />}</AnimatePresence>
    </div>
  );
}